// src/components/ErrorBoundary.jsx - Updated with Lucide icons
import { Component, Suspense } from 'react';
import { RefreshCw, Home } from 'lucide-react';
import logger from '../../utils/core/logger';
import { trackException } from '../../utils/analytics/posthog';


class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    this.logError(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset error state when navigating to a new route so a single component
    // error doesn't permanently block the entire route tree.
    if (this.state.hasError && prevProps.location !== this.props.location) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  logError = (error, errorInfo) => {
    logger.error('Error caught by boundary:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.props.userId || 'anonymous'
    });

    // Forward to PostHog Error Tracking
    trackException(error, {
      boundary_id: this.props.userId || 'unknown',
      component_stack: errorInfo.componentStack?.slice(0, 500),
      url: window.location.href,
    });
  };

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          retryCount={this.state.retryCount}
          isDevelopment={import.meta.env.DEV || (typeof window !== 'undefined' && !!window.Capacitor)}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, onRetry, retryCount, isDevelopment }) {
  const canRetry = retryCount < 3;
  const isNetworkError = error?.message?.includes('fetch') || error?.message?.includes('network');

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
      background: '#1a1410',
    }}>
      <div style={{ maxWidth: '360px', width: '100%', textAlign: 'center' }}>
        <p style={{
          fontSize: '11px',
          fontFamily: 'monospace',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'rgba(255,184,94,0.45)',
          marginBottom: '20px',
        }}>
          Something went wrong
        </p>

        <p style={{
          fontSize: '15px',
          color: 'rgba(245,237,224,0.55)',
          lineHeight: 1.65,
          marginBottom: '28px',
        }}>
          {isNetworkError
            ? "Having trouble connecting. Check your internet connection and try again."
            : "An unexpected error occurred. We've been notified."
          }
        </p>

        {isDevelopment && error && (
          <details style={{ textAlign: 'left', marginBottom: '24px' }}>
            <summary style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.25)',
              cursor: 'pointer',
              marginBottom: '8px',
              fontFamily: 'monospace',
            }}>
              Error details
            </summary>
            <pre style={{
              fontSize: '11px',
              color: 'rgba(255,100,100,0.6)',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              maxHeight: '120px',
              lineHeight: 1.5,
            }}>
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {canRetry && (
            <button
              onClick={onRetry}
              style={{
                background: 'none',
                border: '1px solid rgba(255,184,94,0.25)',
                borderRadius: '10px',
                color: '#ffb85e',
                cursor: 'pointer',
                padding: '10px 20px',
                fontSize: '14px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
            >
              <RefreshCw size={14} /> Try again
            </button>
          )}

          <button
            onClick={() => window.location.replace('/')}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              padding: '10px 20px',
              fontSize: '14px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              fontFamily: 'inherit',
            }}
          >
            <Home size={14} /> Go home
          </button>
        </div>

        {retryCount > 0 && (
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '16px' }}>
            Retry {retryCount} of 3
          </p>
        )}
      </div>
    </div>
  );
}

export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Loading fallback component for Suspense
function LoadingFallback({ message = "Loading..." }) {
  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#1a1410',
    }}>
      <p style={{
        fontSize: '13px',
        color: 'rgba(245,237,224,0.3)',
        letterSpacing: '0.02em',
      }}>
        {message}
      </p>
    </div>
  );
}

// Higher-order component that wraps components with both ErrorBoundary and Suspense
export const withAsyncErrorBoundary = (
  Component,
  {
    errorBoundaryProps = {},
    loadingMessage = "Loading..."
  } = {}
) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Suspense fallback={<LoadingFallback message={loadingMessage} />}>
        <Component {...props} />
      </Suspense>
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withAsyncErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;